import { IAddress, IUser } from '~/models';
import { paymentsApi } from './paymentsApi';
import {
  IAddCardPayload,
  IConnectPlaidPayload,
  IFinalizePlaidVerifyPayload,
  IMintVerifyLinkTokenPayload,
} from './types';

export const preparePaymentUserBody = (
  user?: IUser | null,
  selectedUser?: IUser | null,
): Record<string, string> => {
  const address: IAddress = user?.address?.[0] || ({} as IAddress);
  const selectedUserAddress: IAddress = selectedUser?.address?.[0] || ({} as IAddress);

  if (selectedUser && user?.id !== selectedUser?._id) {
    return {
      first_name: selectedUser?.first_name || '',
      last_name: selectedUser?.last_name || '',
      full_name: selectedUser?.full_name || '',
      email: selectedUser?.email_id || '',
      formatted_address: selectedUserAddress?.formatted_address || '',
      city: selectedUserAddress?.city || '',
      state: selectedUserAddress?.state || '',
      country: selectedUserAddress?.country || '',
      postal_code: selectedUserAddress?.postal_code || '',
      phone_number: selectedUser?.contact_number || '',
      street_number: selectedUserAddress?.street_number || '',
      street: selectedUserAddress?.street || '',
    };
  }

  return {
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    full_name: user?.full_name || '',
    email: user?.email_id || '',
    formatted_address: address?.formatted_address || '',
    city: address?.city || '',
    state: address?.state || '',
    country: address?.country || '',
    postal_code: address?.postal_code || '',
    phone_number: user?.contact_number || '',
    street_number: address?.street_number || '',
    street: address?.street || '',
  };
};

type PlaidAccountMetadata = Record<string, unknown> & {
  verification_status?: unknown;
  verificationStatus?: unknown;
};

type PlaidMetadata = Record<string, unknown> & {
  account?: unknown;
  accounts?: unknown;
};

const normalizePlaidAccountMetadata = (account: unknown) => {
  if (!account || typeof account !== 'object' || Array.isArray(account)) {
    return account;
  }

  const accountMetadata = account as PlaidAccountMetadata;

  if (
    accountMetadata.verificationStatus !== undefined ||
    accountMetadata.verification_status === undefined
  ) {
    return accountMetadata;
  }

  return {
    ...accountMetadata,
    verificationStatus: accountMetadata.verification_status,
  };
};

export const normalizePlaidMetadata = (metadata: unknown) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return metadata;
  }

  const plaidMetadata = metadata as PlaidMetadata;
  const normalizedMetadata = { ...plaidMetadata };

  if ('account' in plaidMetadata) {
    normalizedMetadata.account = normalizePlaidAccountMetadata(plaidMetadata.account);
  }

  if (Array.isArray(plaidMetadata.accounts)) {
    normalizedMetadata.accounts = plaidMetadata.accounts.map(normalizePlaidAccountMetadata);
  }

  return normalizedMetadata;
};

const getPaymentMethods = (officeId?: string) => paymentsApi.getPaymentMethods(officeId);

const mintConnectLinkToken = (officeId?: string) => paymentsApi.mintConnectLinkToken(officeId);

const connectPlaid = ({
  payload,
  user,
  billingOffice,
  officeId,
}: {
  payload: IConnectPlaidPayload;
  user?: IUser | null;
  billingOffice?: IUser | null;
  /** The location the bank account is for; the selected office when omitted. */
  officeId?: string;
}) =>
  paymentsApi.connectPlaid(
    {
      metadata: normalizePlaidMetadata(payload.metadata),
      publicToken: payload.publicToken,
      user: preparePaymentUserBody(user, billingOffice),
      ...(payload.achMandateAuthorized && {
        achMandateAuthorized: payload.achMandateAuthorized,
      }),
    },
    officeId,
  );

const mintVerifyLinkToken = (payload: IMintVerifyLinkTokenPayload, officeId?: string) =>
  paymentsApi.mintVerifyLinkToken(payload, officeId);

const finalizePlaidVerify = (payload: IFinalizePlaidVerifyPayload, officeId?: string) =>
  paymentsApi.finalizePlaidVerify(payload, officeId);

const addCard = ({
  payload,
  user,
  billingOffice,
  officeId,
}: {
  payload: IAddCardPayload;
  user?: IUser | null;
  billingOffice?: IUser | null;
  /** The location the card is for; the selected office when omitted. */
  officeId?: string;
}) =>
  paymentsApi.addCard(
    {
      token: payload.token,
      user: preparePaymentUserBody(user, billingOffice),
    },
    officeId,
  );

const deletePaymentMethod = (paymentId: string, officeId?: string) =>
  paymentsApi.deletePaymentMethod(paymentId, officeId);

const selectPaymentMethod = (paymentId: string, officeId?: string) =>
  paymentsApi.selectPaymentMethod(paymentId, officeId);

export const paymentsService = {
  getPaymentMethods,
  mintConnectLinkToken,
  connectPlaid,
  mintVerifyLinkToken,
  finalizePlaidVerify,
  addCard,
  deletePaymentMethod,
  selectPaymentMethod,
};
